import React, { useState } from 'react';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useDeleteAccommodationPlaceV2Mutation } from '@graphql/generated';
import { TravelMapBlockType } from '../Accommodations.types';
import { useEventPlaceActions } from '@shared/utils/eventPlace';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useUpdateAccommodationsSortOrder } from '@apps/admin/common/hooks/useUpdateAccommodationsSortOrder';

interface AccommodationsDeleteReturnType {
  isLoading: boolean;
  onDelete: (itemType: TravelMapBlockType, id?: string | null, pointOnMapId?: string | null) => void | Promise<void>;
}

type AccommodationsDeleteProps = {
  handleCloseOnSuccess: () => void;
};

export const useAccommodationsDelete = (props: AccommodationsDeleteProps): AccommodationsDeleteReturnType => {
  const [toastMessage, setToastMessage] = useState('');
  const { handleCloseOnSuccess } = props;
  const { toast } = useToast();

  const { tq } = useTranslation('websiteEditor');
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { updateSortOrder } = useUpdateAccommodationsSortOrder(() => updateWebsitePreview());

  const onError = useEventCallback(() => toast(`${toastMessage}`, { icon: <WarningTriangle /> }));
  const onCompleted = useEventCallback(() => handleCloseOnSuccess());

  const { deleteEventPlace, isLoading: waitingForDeletePointOnMap } = useEventPlaceActions({ onCompleted, onError, updateWebsitePreview });

  const [deleteAccommodationPlace, { loading: waitingForAccommodationPlaceDelete }] = useDeleteAccommodationPlaceV2Mutation({ onCompleted, onError });

  const getOnDeleteFn = async (itemType: TravelMapBlockType, id?: string | null, pointOnMapId?: string | null) => {
    switch (itemType) {
      case TravelMapBlockType.travelMapVenue:
        setToastMessage(tq`An error occurred while deleting the venue. Please try again.`);
        await deleteEventPlace({ payload: { id, pointOnMapId } });
        break;
      case TravelMapBlockType.travelMapAccommodationsRoomblock:
        if (!id) return;
        setToastMessage(tq`An error occurred while deleting the hotel. Please try again.`);
        deleteAccommodationPlace({ variables: { id: id }, refetchQueries: () => ['AccommodationHotels'], awaitRefetchQueries: true }).then(() => {
          updateSortOrder({ accommodationsPlaceIds: [id], action: 'remove' });
        });
        break;
      case TravelMapBlockType.travelMapAccommodationsCustom:
      case TravelMapBlockType.travelMapAccommodationsHotel:
        if (!id) return;
        setToastMessage(tq`An error occurred while deleting the hotel. Please try again.`);
        deleteAccommodationPlace({ variables: { id: id }, refetchQueries: () => ['AccommodationHotels'], awaitRefetchQueries: true }).then(() => {
          updateSortOrder({ accommodationsPlaceIds: [id], action: 'remove' });
        });
        break;
      default:
        break;
    }
  };

  return {
    onDelete: getOnDeleteFn,
    isLoading: waitingForDeletePointOnMap || waitingForAccommodationPlaceDelete,
  };
};
